import { groupPictureStyles } from '../generation/pictureStyles';
import { usePictureStyles } from '../generation/usePictureStyles';

/** One style's standing with the brand. Neutral is "in neither list". */
type Preference = 'neutral' | 'use' | 'avoid';

const NEXT: Readonly<Record<Preference, Preference>> = {
  neutral: 'use',
  use: 'avoid',
  avoid: 'neutral',
};

const STATE_WORD: Readonly<Record<Preference, string>> = {
  neutral: 'Neutral',
  use: 'Use',
  avoid: 'Avoid',
};

interface PictureStylePrefsProps {
  /** `brand_identity.picture_styles`: the styles the AI may choose from. */
  use: readonly string[];
  /** `brand_identity.avoid_picture_styles`: the styles it must not choose. */
  avoid: readonly string[];
  onChange: (next: { use: string[]; avoid: string[] }) => void;
  disabled?: boolean;
}

/**
 * "Picture styles": one chip per registry style, each click cycling Neutral,
 * Use, Avoid. The two lists never share a key (the API refuses that with a
 * 422), because a style is moved out of one list as it enters the other.
 *
 * Keys on file that the registry no longer carries are kept as they are, so a
 * save never drops something this build cannot show. A failed registry load
 * shows a line instead of chips; the lists on file are untouched.
 */
export function PictureStylePrefs({
  use,
  avoid,
  onChange,
  disabled = false,
}: PictureStylePrefsProps) {
  const { pictureStyles, isLoading, error } = usePictureStyles();
  const families = groupPictureStyles(pictureStyles);

  function preferenceOf(key: string): Preference {
    if (use.includes(key)) return 'use';
    if (avoid.includes(key)) return 'avoid';
    return 'neutral';
  }

  function cycle(key: string) {
    const next = NEXT[preferenceOf(key)];
    const nextUse = use.filter((entry) => entry !== key);
    const nextAvoid = avoid.filter((entry) => entry !== key);
    if (next === 'use') nextUse.push(key);
    if (next === 'avoid') nextAvoid.push(key);
    onChange({ use: nextUse, avoid: nextAvoid });
  }

  return (
    <fieldset className="chip-group picture-style-field" disabled={disabled}>
      <legend className="field__label">Picture styles</legend>
      <p className="field__hint">Leave all neutral to let the AI use any style.</p>

      {error ? (
        <p className="field__hint">The picture styles could not be loaded.</p>
      ) : isLoading ? (
        <p className="field__hint" role="status">
          Loading picture styles
        </p>
      ) : (
        families.map((family) => (
          <div className="picture-style-field__family" key={family.key}>
            <span className="picture-style-field__family-label">{family.label}</span>
            <div className="picture-style-field__chips">
              {family.styles.map((style) => {
                const preference = preferenceOf(style.key);
                return (
                  <button
                    key={style.key}
                    type="button"
                    className={`chip style-pref style-pref--${preference}`}
                    title={style.blurb}
                    aria-label={`${style.label}: ${STATE_WORD[preference]}`}
                    onClick={() => cycle(style.key)}
                  >
                    <span>{style.label}</span>
                    {preference !== 'neutral' ? (
                      <span className="style-pref__state" aria-hidden="true">
                        {STATE_WORD[preference]}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        ))
      )}
    </fieldset>
  );
}
